import { useMatches } from '@hub/router';
import { useContext } from 'react';
import { BannerContext, filterByRoute, selectGlobal, sortByPriority } from '../BannerContext';
import type { BannerDefinition } from '../banner.types';

type BannerFilter = 'global' | 'route';

export interface InboxBanner extends BannerDefinition {
  dismissed: boolean;
}

function useBannerContext() {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error('useBanners must be used within a BannerProvider');
  }
  return context;
}

export function useBanners(filter: BannerFilter = 'global') {
  const { banners, dismissedIds } = useBannerContext();
  const matches = useMatches();

  const visible = banners.filter((b) => !dismissedIds.has(b.id));
  if (filter === 'global') {
    return selectGlobal(visible);
  }
  const leafPath = matches.at(-1)?.pathname ?? '';
  return sortByPriority(filterByRoute(visible, leafPath));
}

export function useBannerInbox(): InboxBanner[] {
  const { banners, dismissedIds } = useBannerContext();

  return selectGlobal(banners).map((banner) => ({
    ...banner,
    dismissed: dismissedIds.has(banner.id),
  }));
}

export function useBannerActions() {
  const { addBanner, removeBanner, dismissBanner } = useBannerContext();
  return { addBanner, removeBanner, dismissBanner };
}
